import { BodyLong, Heading, Link, Table, VerticalSpace } from '@elhub/ds-components'

import React from 'react'
import ReactJson from 'react-json-view'

const json = {
  data: [
    {
      attributes: {
        dataset: 'consumption_per_group_mba_hour',
        entity: 'price-areas',
        updateTime: '2024-08-24T15:55:00+02:00',
        usageDate: '2024-08-18'
      },
      id: '20240818_consumption_per_group_mba_hour',
      links: {
        href: 'https://api.elhub.no/energy-data/v0/price-areas?dataset=consumption_per_group_mba_hour&startDate=2024-08-18&endDate=2024-08-19'
      },
      type: 'updates'
    },
    {
      attributes: {
        dataset: 'consumption_per_group_mba_hour',
        entity: 'price-areas',
        updateTime: '2024-08-24T16:05:48+02:00',
        usageDate: '2024-08-19'
      },
      id: '20240819_consumption_per_group_mba_hour',
      links: {
        href: 'https://api.elhub.no/energy-data/v0/price-areas?dataset=consumption_per_group_mba_hour&startDate=2024-08-19&endDate=2024-08-20'
      },
      type: 'updates'
    }
  ],
  links: {
    self: 'https://api.elhub.no/energy-data/v0/updates'
  },
  meta: {
    created: '2024-08-24T18:40:36+02:00',
    lastUpdated: '2024-08-24T18:40:36+02:00'
  }
}

const attributes = [
  { Attribute: 'dataset', Type: 'String', Description: 'Dataset for which the updates are published.' },
  {
    Attribute: 'entity',
    Type: 'String',
    Description:
      'Resource on which the respective dataset is grouped under. Eg: if the dataset attribute is consumption_per_group_mba_hour then the entity  will be price-areas'
  },
  { Attribute: 'updateTime', Type: 'Array', Description: 'Time on which the data is updated' },
  { Attribute: 'usageDate', Type: 'String', Description: 'Usage date for which the dataset is updated' },
  {
    Attribute: 'links.href',
    Type: 'String',
    Description:
      'Link to the respective API endpoint to fetch the updated aggregates of the dataset for the usage date'
  }
]

const Updates = () => {
  return (
    <>
      <Heading>Updates</Heading>
      <VerticalSpace />
      <BodyLong size='small'>
        This API endpoint enables clients to retrieve updates to datasets efficiently.
      </BodyLong>
      <VerticalSpace />
      <BodyLong size='small'>
        Updates to a dataset may occur on any usage date within the past three years. By using this endpoint,
        clients can fetch only the data that has been updated since their last request, rather than retrieving
        the entire dataset each time. This approach ensures that clients always have the latest information
        while minimizing data transfer and processing time.
      </BodyLong>
      <VerticalSpace />
      <BodyLong size='small'>
        <b>Recommendations for Using this Endpoint:</b>
        <VerticalSpace />
        Updates are based on usage dates and are published immediately after the respective dataset aggregates
        are updated. Each update includes details such as the <code>usage_date</code>, <code>dataset</code>,{' '}
        <code>entity</code>, and an API endpoint link to fetch the updated data for that specific dataset and
        usage date.
        <ul>
          <VerticalSpace />
          <li>
            You can filter these updates using the <code>lastUpdated</code> parameter, which corresponds to
            the <code>updateTime</code> of the dataset. This allows you to retrieve only the most recent
            changes since your last request.
          </li>
          <VerticalSpace />
          <li>
            There might be a scenario where an update to a dataset is made, but the commit process hasn't
            completed by the time a client queries the endpoint. If the client queries during this brief
            window, they might miss that update. To avoid missing any updates, it's recommended to use a
            slightly earlier{' '}
            <strong>
              <code>lastUpdated</code>
            </strong>{' '}
            time in subsequent requests, ensuring that any updates delayed in committing are still captured.
          </li>
        </ul>
        <VerticalSpace />
        <b>Examples:</b>
        <ol>
          <li>
            <strong>First-time Query at T:</strong>
            <br />
            <code>/updates?lastUpdated=T</code>
            <br />
            This will fetch all records that have been updated since time T.
          </li>
          <VerticalSpace />
          <li>
            <strong>Scheduled Daily Query at T:</strong>
            <br />
            <code>/updates?lastUpdated=T-1</code>
            <br />
            Schedule a daily query at the same time every day. T-1 represents one hour before the previous
            sync time. This will fetch all records that have been updated since then.
            <br />
            <i>Processing Logic:</i> Compare the <code>usage_date</code> and <code>update_time</code> fields
            and process only the records that are missing or need updating.
          </li>
        </ol>
      </BodyLong>
      <VerticalSpace />
      <BodyLong size='small'>
        <b>Example request to query the updates:</b>{' '}
        <Link
          variant='primary'
          href='https://api.elhub.no/energy-data/v0/updates?dataset=CONSUMPTION_PER_GROUP_MBA_HOUR&lastUpdated=2023-09-01'
        >
          https://api.elhub.no/energy-data/v0/updates?dataset=CONSUMPTION_PER_GROUP_MBA_HOUR&lastUpdated=2023-09-01
        </Link>
      </BodyLong>
      <VerticalSpace />
      <BodyLong size='small'>
        Please note that the lastUpdated query parameter should adhere to the ISO 8601 date format, either as
        "yyyy-MM-dd" or with a timezone offset in the "yyyy-MM-dd'T'HH:mm:ssXXX" pattern. When using the
        latter pattern in browser, remember to URL encode the offset, for example, "2023-01-01T00:00:00+02:00"
        should be encoded as "2023-01-01T00:00:00%2B02:00."
      </BodyLong>
      <VerticalSpace />
      <BodyLong size='small'>
        <b>The "yyyy-MM-dd" date format aligns with midnight.</b>
      </BodyLong>
      <VerticalSpace />
      <BodyLong size='small'>
        <b>lastUpdated time is inclusive in the response.</b>
      </BodyLong>
      <VerticalSpace />
      <BodyLong size='small'>
        If the lastUpdated query param is not provided, updates from the last 24 hours will be returned as
        response.
      </BodyLong>
      <VerticalSpace />
      <BodyLong size='small'>Sample response:</BodyLong>
      <ReactJson src={json} collapsed name={false} sortKeys />
      <VerticalSpace />
      <Table size='small'>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader scope='col'>Attribute</Table.ColumnHeader>
            <Table.ColumnHeader scope='col'>Type</Table.ColumnHeader>
            <Table.ColumnHeader scope='col'>Description</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {attributes.map(({ Attribute, Type, Description }) => (
            <Table.Row key={Attribute}>
              <Table.HeaderCell scope='row'>{Attribute}</Table.HeaderCell>
              <Table.DataCell>{Type}</Table.DataCell>
              <Table.DataCell>{Description}</Table.DataCell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </>
  )
}

export default Updates
